import { QueryModel } from '@carwash-project/modules/data-access/common';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// =============================================================================
// FORMATOS DE FECHA - Punto único de modificación para todo el proyecto
// =============================================================================

/** Fecha para backend/API (yyyy/MM/dd) */
export const DATE_FORMAT_BACKEND = 'yyyy/MM/dd';

/** Fecha con hora para backend/API (yyyy/MM/dd HH:mm:ss) */
export const DATE_TIME_FORMAT_BACKEND = 'yyyy/MM/dd HH:mm:ss';

/** Fecha para mostrar (yyyy/MM/dd) */
export const DATE_FORMAT_DISPLAY = 'yyyy/MM/dd';

/** Fecha corta para mostrar (dd/MM/yyyy) - formato día/mes/año */
export const DATE_FORMAT_DISPLAY_SHORT = 'dd/MM/yyyy';

/** Fecha con hora para mostrar (dd/MM/yyyy HH:mm:ss) */
export const DATE_TIME_FORMAT_DISPLAY = 'dd/MM/yyyy HH:mm:ss';

/** Vencimiento de tarjetas (MM/yyyy) */
export const CARD_EXPIRATION_FORMAT = 'MM/yyyy';

/** Formato para ng-zorro date-picker */
export const NZ_DATE_FORMAT = 'yyyy/MM/dd';

/** Formato para ng-zorro date-picker con hora */
export const NZ_DATE_TIME_FORMAT = 'yyyy/MM/dd HH:mm:ss';

export const currencyFormatter = new Intl.NumberFormat('es-GT', {
  style: 'currency',
  currency: 'GTQ',
  minimumFractionDigits: 1,
});

export const plateFormatter = (plate: string): [string, string] => {
  const length = plate.length;

  switch (length) {
    case 7:
      return [plate[0], plate.slice(1)];

    case 8:
      return [plate.slice(0, 2), plate.slice(2)];

    default:
      return [plate.slice(0, 3), plate.slice(3)];
  }
};

export const formatHoursFn = (date: Date, hour12: boolean = true) => {
  return new Date(date).toLocaleString('es', {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  });
};

/** Formatea fecha. Por defecto usa DATE_FORMAT_DISPLAY. Pasar formato custom para casos especiales. */
export const formatDateFn = (
  date: Date | number | string,
  formatStr: string = DATE_FORMAT_DISPLAY
) => {
  if (typeof date === 'string') return date;
  return format(date, formatStr, { locale: es });
};

/** Fecha para enviar al backend (yyyy/MM/dd) */
export const formatDateForBackend = (date: Date | number | string) =>
  formatDateFn(date, DATE_FORMAT_BACKEND);

/** Fecha con hora para enviar al backend (yyyy/MM/dd HH:mm:ss) */
export const formatDateFnIso = (
  date: Date | number | string,
  includeTime: boolean = true
) => {
  if (typeof date === 'string') return date;
  return format(
    date,
    includeTime ? DATE_TIME_FORMAT_BACKEND : DATE_FORMAT_BACKEND,
    { locale: es }
  );
};

/** Fecha para mostrar en UI (dd/MM/yyyy) - formato día/mes/año */
export const formatDateDisplayShort = (date: Date | number | string) =>
  formatDateFn(date, DATE_FORMAT_DISPLAY_SHORT);

/** Fecha con hora para mostrar en UI (dd/MM/yyyy HH:mm:ss) */
export const formatDateTimeDisplay = (date: Date | number | string) =>
  formatDateFn(date, DATE_TIME_FORMAT_DISPLAY);

/** Vencimiento de tarjetas (MM/yyyy) */
export const formatCardExpiration = (date: Date | number | string) =>
  formatDateFn(date, CARD_EXPIRATION_FORMAT);

export const sortModeFn = (event: string | null) => {
  if (event === 'descend') {
    return 'DESC';
  }

  if (event === 'ascend') {
    return 'ASC';
  }

  return null;
};

type QueryModelMutable = {
  -readonly [key in keyof QueryModel]: QueryModel[key];
};

export const configSortFilter = <T extends QueryModelMutable>(
  filters: T,
  sortKey: string | undefined,
  mode: string | null
) => {
  let currentFilters = {
    ...filters,
  };
  if (!sortKey) {
    return currentFilters;
  }

  const sortMode = sortModeFn(mode);

  if (sortMode) {
    currentFilters = {
      ...currentFilters,
      sort: `${sortKey} ${sortMode}`,
    };
  } else if (!sortMode && currentFilters.sort) {
    delete currentFilters.sort;
  }

  return currentFilters;
};
